import { WorkspaceSidePanelTab } from "./WorkspaceSidePanelTab"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import {
    selectActivatingProjectId,
    selectActiveWorkspaceId,
    selectIdRenaming,
} from "features/data-management/workspace/WorkspaceManagementSlice"
import { useMemo, useState, useRef } from "react"
import { createContextMenuSections } from "features/data-management/workspace/module"
import {
    WorkspaceEntityType,
    WorkspaceProjectTabProps,
} from "features/data-management/workspace/types"
import { Pen4 } from "@nova/component-icons"
import { useWorkspacePermissions } from "features/data-management/workspace/useWorkspacePermissions"
import classNames from "classnames"
import { useWorkspaceProjectDragAndDrop } from "../../useWorkspaceProjectDragAndDrop"
import { getColorFromId } from "@nova/util-strings"
import { defaultProjectName } from "features/data-management/workspace/constants"
import { useLoadingText } from "../../useLoadingText"
import { setActiveContextMenu } from "features/popups/slice"
import { selectActiveContextMenu } from "features/popups/contextMenu"

export const WorkspaceProjectTab = ({
    project,
    categoryKey,
    isInFavorites,
    isFavorite,
    handleActivateProject,
    // handleMoveTo,
    handleBeginRenaming,
    handleConfirmRenaming,
    handleCancelRenaming,
    handleOpenShareModal,
    handleToggleFavorite,
    handleDelete,
}: WorkspaceProjectTabProps) => {
    const color = getColorFromId(project.id)
    const type = WorkspaceEntityType.Project

    const dispatch = useAppDispatch()
    const [isHovering, setIsHovering] = useState(false)
    const { isEditor } = useWorkspacePermissions(project.id)
    const activeWorkspaceSidePanelContextMenu = useAppSelector(
        selectActiveContextMenu("WorkspaceSidePanel")
    )
    const contextMenuIsForThisProject =
        activeWorkspaceSidePanelContextMenu?.data.entityId === project.id
    const idRenaming = useAppSelector(selectIdRenaming())
    const isRenaming = idRenaming === categoryKey
    const isActivatingThisProject = useAppSelector(selectActivatingProjectId()) === project.id
    const loadingText = useLoadingText(isActivatingThisProject)
    const workspaceId = useAppSelector(selectActiveWorkspaceId())

    const contextMenuSections = useMemo(
        () =>
            createContextMenuSections({
                id: project.id,
                key: categoryKey,
                title: project.name || defaultProjectName,
                isFavorite,
                onToggleFavorite: handleToggleFavorite,
                onBeginRenaming: isEditor ? handleBeginRenaming : undefined,
                // onMoveTo: ,
                onOpenShareModal: handleOpenShareModal,
                onOpenInNewTab: (id) => {
                    window.open(`${window.origin}/workspace/${workspaceId}/project/${id}`)
                },
                onDelete: isEditor ? (id, name) => handleDelete(id, name, type) : undefined,
            }),
        [
            project.id,
            project.name,
            categoryKey,
            isFavorite,
            handleToggleFavorite,
            isEditor,
            handleBeginRenaming,
            handleOpenShareModal,
            workspaceId,
            handleDelete,
            type,
        ]
    )

    const { dragRef, anythingIsDragging } = useWorkspaceProjectDragAndDrop({
        id: project.id,
        isEditor,
        isRenaming,
        isInFavorites,
    })

    /**
     * @param force
     * If `true`, function will force-show the context menu.
     * If `false`, function will force-hide the context menu.
     * If `undefined`, function will toggle the context menu.
     */
    const handleToggleContextMenu = (force?: boolean) => {
        const shouldHide = (contextMenuIsForThisProject && !force) || force === false

        if (shouldHide) setIsHovering(false)

        dispatch(
            setActiveContextMenu(
                shouldHide
                    ? undefined
                    : {
                          type: "WorkspaceSidePanel",
                          data: {
                              entityId: project.id,
                              isFavorite: isInFavorites,
                              fixedPosition: false,
                          },
                          position: { top: 0, left: 0 },
                      }
            )
        )
    }

    const projectDragRef = useRef<HTMLDivElement>(null)
    dragRef(projectDragRef)

    return (
        <div ref={projectDragRef} className={classNames({ "opacity-20": anythingIsDragging })}>
            <WorkspaceSidePanelTab
                title={isActivatingThisProject ? loadingText : project.name || defaultProjectName}
                id={categoryKey}
                icon={Pen4}
                iconColor={color}
                isRenaming={isRenaming}
                handleConfirmRename={(_, name) => handleConfirmRenaming(project.id, name, type)}
                handleCancelRename={handleCancelRenaming}
                contextMenuSections={contextMenuSections}
                handleToggleContextMenu={handleToggleContextMenu}
                isHovering={isHovering}
                clickWillNavigate={true}
                shouldShowArrow
                handleClick={() => handleActivateProject(project.id)}
                handleRightClick={(e) =>
                    dispatch(
                        setActiveContextMenu({
                            type: "WorkspaceSidePanel",
                            data: {
                                entityId: project.id,
                                isFavorite: isInFavorites,
                                fixedPosition: true,
                            },
                            position: { top: e.clientY, left: e.clientX },
                        })
                    )
                }
                onMouseEnter={() => setIsHovering(true)}
                onMouseLeave={() => setIsHovering(false)}
            />
        </div>
    )
}
